import { datadogRum } from '@datadog/browser-rum';
import { addNextjsError } from '@datadog/browser-rum-nextjs';
import { RUM_ENABLED } from './env';

/**
 * The app's touch points with the RUM SDK after `instrumentation-client.ts`
 * has initialised it. Every call is a no-op when RUM is off so callers need no
 * gating of their own.
 */

/** The organization a user belongs to, as `GET /users/organization` returns it. */
export interface RumOrganization {
  id: string;
  name: string;
}

/**
 * Datadog `usr.id` = the same email Langfuse traces carry as `user.id`
 * (src/telemetry/tracing.ts), so a user can be followed from a replay to the
 * agent trace of the turn they were watching, and back.
 *
 * The organization rides along as `usr.org_id` / `usr.org_name`. Without it a
 * session can only be found by the individual who produced it; with it, every
 * RUM query can be asked of a whole customer account, which is the unit a
 * conversation about renewal or adoption is actually about. `org_name` is the
 * readable facet, `org_id` the stable one — a firm that gets renamed keeps its
 * id, so historical sessions still group with the current ones.
 *
 * `usr.name` is the label Datadog prints on a session in the Replay list and
 * the player; without it every session reads "Public user". The product has no
 * display name for a user — sign-in is an email code, and the users table
 * holds no name column — so the email is the only readable identity there is.
 */
export function identifyRumUser(email: string | null | undefined, org?: RumOrganization | null): void {
  if (!RUM_ENABLED) return;
  if (!email) {
    datadogRum.clearUser();
    return;
  }
  datadogRum.setUser({
    id: email,
    name: email,
    email,
    ...(org && { org_id: org.id, org_name: org.name }),
  });
}

// Global context so a conversation id is a facet on every event of the session
// (the view name only says `/c/[conversationId]`); it is also the Langfuse
// `session.id`, which closes the loop the other way.
export function setRumConversation(conversationId: string | null): void {
  if (!RUM_ENABLED) return;
  if (conversationId) {
    datadogRum.setGlobalContextProperty('conversation_id', conversationId);
  } else {
    datadogRum.removeGlobalContextProperty('conversation_id');
  }
}

/**
 * The product actions worth counting, as opposed to the clicks the SDK already
 * records on its own.
 *
 * A click tells you someone pressed the Analysis tab. It does not tell you they
 * ran a tolerance analysis and it failed, which is the thing that makes a user
 * give up. These names are the funnel: each is a real unit of work a customer
 * came here to do, and each failure name is the same unit not completing.
 *
 * Counting convention: the five long-running actions each emit `started` and
 * then exactly one of `completed` or `failed`. **Success is the `completed`
 * count, never `started - failed`.** Subtraction was the original design and it
 * is wrong: every way a run can end without an outcome — the user stopping it,
 * a newer run superseding it, a dropped stream, an analysis the engine does not
 * implement — would silently land in the success bucket, and the analysis hook
 * re-fires on every settings change, so that inflation is systematic rather
 * than rare. With explicit outcomes those runs sit in
 * `started - completed - failed`, which reads honestly as abandoned.
 *
 * Import and export have no start event because they cannot be abandoned: the
 * success name IS the outcome, emitted only once the work has landed.
 *
 * A run the USER stopped is neither `completed` nor `failed`, and neither is one
 * the app refused locally before any work started. Counting a stop as a failure
 * would blame the product for something it did not get wrong; counting it as a
 * success would hide a user giving up, which is the exact signal this is for.
 *
 * Kept as a closed union deliberately. Free-form strings drift into three
 * spellings of the same event, and a Datadog facet cannot tell them apart
 * afterwards.
 */
export type RumEventName =
  | 'agent turn started'
  | 'agent turn completed'
  | 'agent turn failed'
  | 'analysis started'
  | 'analysis completed'
  | 'analysis failed'
  // Format-neutral on purpose: the import button takes both .zmx and CAD .step
  // files through one path, so a name that said "zemax" would mislabel every
  // STEP import. The format rides in the event's context instead.
  | 'design imported'
  | 'design import failed'
  | 'design exported'
  | 'design export failed'
  | 'optimization started'
  | 'optimization completed'
  | 'optimization failed'
  | 'tolerance analysis started'
  | 'tolerance analysis completed'
  | 'tolerance analysis failed'
  | 'zosapi script started'
  | 'zosapi script completed'
  | 'zosapi script failed'
  // The Macros tab of the ZOS-API panel: a firm-library ZPL macro run as
  // written, or handed to the Script Assistant for conversion.
  | 'zpl macro started'
  | 'zpl macro completed'
  | 'zpl macro failed'
  | 'zpl macro converted'
  | 'zpl macro imported'
  | 'zpl macro deleted'
  | 'zpl macro written'
  | 'zpl macro edited';

/**
 * Record a product action. Context keys become `@context.*` facets in Datadog,
 * so pass the few dimensions worth slicing by (engine, analysis id, a short
 * error reason) and nothing user-authored — a prompt or a file's contents does
 * not belong in an analytics event.
 */
export function trackRumEvent(name: RumEventName, context?: Record<string, unknown>): void {
  if (!RUM_ENABLED) return;
  datadogRum.addAction(name, context);
}

/**
 * Shorten an unknown thrown value into a bounded reason string for an event's
 * context. The full error still reaches RUM through `addError` and the error
 * boundaries; this is only the label a funnel is grouped by.
 */
export function rumErrorReason(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.slice(0, 200);
}

/**
 * The RUM session id — the join key from a feedback report to its Datadog
 * session, which exists whether or not the session was sampled for replay.
 * Undefined when RUM is off or the SDK has not started a session yet.
 */
export function rumSessionId(): string | undefined {
  if (!RUM_ENABLED) return undefined;
  return datadogRum.getInternalContext()?.session_id;
}

/** Deep link to the current session's replay; undefined when RUM is off or the session was not sampled for replay. */
export function sessionReplayLink(): string | undefined {
  if (!RUM_ENABLED) return undefined;
  return datadogRum.getSessionReplayLink();
}

/**
 * The organization lookup failing leaves the session tagged with the user but
 * no firm, so every firm-level RUM query silently undercounts that session.
 * Reported as a non-fatal RUM error so the undercount is diagnosable from
 * Datadog itself rather than invisible.
 */
export function reportOrganizationLookupFailure(error: unknown): void {
  if (!RUM_ENABLED) return;
  datadogRum.addError(error, { source: 'organization-lookup' });
}

/** Errors caught by the app's error boundaries never reach `window.onerror`, so they are reported here. */
export function reportRumError(error: Error & { digest?: string }): void {
  if (!RUM_ENABLED) return;
  addNextjsError(error);
}
